/**
 * OverlayIconButton - Consistent circular icon button for overlay on images/media
 *
 * Used for:
 * - Back buttons on detail pages
 * - Share/action buttons on media carousels and entity headers
 * - Close buttons on modals
 *
 * Standard sizing: 50x50px button with 25x25px icon
 */

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Theme-aware overlay background + text colors. Single source of truth.
 * Light mode: frosted white bg, dark icons. Dark mode: frosted dark bg, light icons.
 */
export const OVERLAY_COLORS = "bg-white/70 text-black shadow-md dark:bg-black/70 dark:text-white dark:shadow-none";

/** Standard icon size for use inside OverlayIconButton (24px mobile, 25px desktop) */
export const OVERLAY_ICON_SIZE = "w-[23px] h-[23px] md:w-[25px] md:h-[25px] flex-shrink-0";

interface OverlayIconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  /** Visual variant: "dark" uses theme-aware colors, "light" uses translucent white */
  variant?: "dark" | "light";
}

export const OverlayIconButton = forwardRef<HTMLButtonElement, OverlayIconButtonProps>(
  ({ children, className, variant = "dark", ...props }, ref) => {
    return (
      <button
        ref={ref}
        type="button"
        className={cn(
          "w-[45px] h-[45px] md:w-[50px] md:h-[50px] flex-shrink-0",
          "grid place-items-center",
          "rounded-full",
          variant === "dark" ? OVERLAY_COLORS : "bg-white/20 text-white",
          className
        )}
        {...props}
      >
        {children}
      </button>
    );
  }
);

OverlayIconButton.displayName = "OverlayIconButton";

/** Small count badge positioned at top-right of an OverlayIconButton */
export function OverlayBadge({ count }: { count: number }): ReactNode {
  if (count <= 0) return null;
  return (
    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-foreground text-background text-ui-micro font-semibold flex items-center justify-center">
      {count}
    </span>
  );
}
